import { Heart } from "lucide-react";
import { Button, cn } from "@city-sage/aurum-avis-labs-components";

interface LikeButtonProps {
  isLiked: boolean;
  likeCount: number;
  onToggleLike: () => void;
  isPending?: boolean;
  variant?: 'light' | 'dark';
  layout?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
}

export const LikeButton = ({
  isLiked,
  likeCount,
  onToggleLike,
  isPending = false,
  variant = 'light',
  layout = 'horizontal',
  size = 'md'
}: LikeButtonProps) => {
  const buttonSizes = {
    sm: "h-8 w-8",
    md: "h-10 w-10", 
    lg: "h-12 w-12"
  };

  const iconSizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6"
  };

  const textSizes = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-sm"
  };

  const buttonClasses = cn(
    "rounded-full transition-all duration-200",
    buttonSizes[size],
    variant === 'dark' ? [
      "backdrop-blur-md",
      isLiked
        ? "bg-red-500/30 text-red-400 hover:bg-red-500/40"
        : "bg-black/30 text-white hover:bg-white/20"
    ] : [
      isLiked
        ? "bg-red-50 text-red-500 hover:bg-red-100"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    ]
  );

  const countClasses = cn(
    "font-medium",
    textSizes[size],
    variant === 'dark' ? "text-white/90" : "text-gray-600"
  );

  if (layout === 'vertical') {
    return (
      <div className="text-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleLike}
          disabled={isPending}
          className={buttonClasses}
        >
          <Heart className={cn(iconSizes[size], isLiked && "fill-current")} />
        </Button>
        <div className={cn(countClasses, "mt-1")}>
          {likeCount}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleLike}
        disabled={isPending}
        className={buttonClasses}
      >
        <Heart className={cn(iconSizes[size], isLiked && "fill-current")} />
      </Button>
      <span className={countClasses}>
        {likeCount}
      </span>
    </div>
  );
}; 